import React, { useState, useEffect, useContext, useRef } from "react";
import Axios from "axios";
import Select from "react-select";
import { API_URL } from "../components/api";
import AuthContext from "../context/AuthContext";

function AddCable() {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  const [clientsRaw, setClientsRaw] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);

  const [client, setClient] = useState(null);
  const [username, setUsername] = useState(null);
  const [pack, setPack] = useState("");
  const [note, setNote] = useState("");
  const [expiry, setExpiry] = useState("");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const isMountedRef = useRef(true);

  const refreshPage = () => window.location.reload(false);

  const getClients = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${API_URL}/radius/subscriptions/clients/onboarding/`,
        {
          headers: {
            Authorization: `Bearer ${authTokens?.access}`,
            "Content-Type": "application/json",
          },
          params: { username: user?.username },
        }
      );

      if (response.status === 200 && isMountedRef.current) {
        const rows = Array.isArray(response.data) ? response.data : [];
        setClientsRaw(rows);
        const uniqueClients = [
          ...new Map(
            rows.map((item) => [
              item.client_id,
              { label: item.fullname, value: item.client_id },
            ])
          ).values(),
        ];
        setClientOptions(uniqueClients);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Failed to load clients.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    getClients();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClientChange = (opt) => {
    setClient(opt);
    setUsername(null);
    if (!opt) {
      setUsernameOptions([]);
      return;
    }
    const filtered = clientsRaw
      .filter((row) => row.client_id === opt.value)
      .map((row) => ({
        label: row.username,
        value: row.subscription_id,
      }));
    setUsernameOptions(filtered);
  };

  const customFilter = (option, inputValue) => {
    const words = inputValue.toLowerCase().split(" ").filter(Boolean);
    return words.every((w) => option.label?.toLowerCase().includes(w));
  };

  const validate = () => !!client && pack.trim() !== "" && expiry.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!validate()) {
      setErrorMsg("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        Action: "Post",
        client_id: client.value,
        notes: note || "",
        package: pack,
        expiry_date: expiry,
        userdealer: user?.username || "",
        subscription_id: username?.value ?? null,
      };

      const resp = await Axios.post(`${API_URL}/radius/subscriptions/cable/`, payload, {
        headers: { Authorization: `Bearer ${authTokens?.access}` },
      });

      if (resp.status === 200) refreshPage();
      else setErrorMsg("Creation failed. Please try again.");
    } catch (err) {
      console.error(err);
      setErrorMsg("Creation failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const packageOptions = [{ value: "Basic", label: "Basic" }];

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "10px 20px" }}>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Client *</label>
          <Select
            placeholder={loading ? "Loading clients..." : "Choose Client"}
            options={clientOptions}
            isLoading={loading}
            onChange={handleClientChange}
            filterOption={customFilter}
            value={client}
            isDisabled={loading || submitting}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Username (optional)</label>
          <Select
            placeholder={
              client
                ? usernameOptions.length
                  ? "Choose Username"
                  : "No usernames for this client"
                : "Select a client first"
            }
            options={usernameOptions}
            onChange={setUsername}
            value={username}
            isDisabled={!client || !usernameOptions.length || submitting}
            filterOption={customFilter}
            isClearable 
          />
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Package *</label>
            <Select
              placeholder="Choose Package"
              options={packageOptions}
              onChange={(opt) => setPack(opt?.value || "")}
              value={packageOptions.find((p) => p.value === pack) || null}
              isDisabled={submitting}
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Expiry Date *</label>
            <input
              type="date"
              className="form-control"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Notes</label>
          <input
            type="text"
            className="form-control"
            placeholder="Notes"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
          />
        </div>

        {errorMsg && (
          <div className="alert alert-danger py-2">{errorMsg}</div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !validate()}
          >
            {submitting ? "Adding..." : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddCable;
